import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Download, Star, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime } from "../../lib/api";
import { PageHeader, StatusBadge, LoadingRows, EmptyState } from "../../components/Shared";
import { Stars } from "./MktShared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function MyOrders() {
  const [orders, setOrders] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [ref, setRef] = useState("");
  const [reviewFor, setReviewFor] = useState(null);
  const [rating, setRating] = useState(5);
  const [rtext, setRtext] = useState("");
  const [busy, setBusy] = useState(false);
  const [downloads, setDownloads] = useState({});
  const navigate = useNavigate();

  const load = () => api.get("/mkt/orders").then((r) => setOrders(r.data.orders)).catch((e) => { toast.error(errMsg(e)); setOrders([]); });
  useEffect(() => { load(); }, []);

  const submitPayment = async () => {
    if (!ref.trim()) { toast.error("Enter payment reference"); return; }
    setBusy(true);
    try { await api.post(`/mkt/orders/${payFor.id}/submit-payment`, { reference: ref.trim() }); toast.success("Payment submitted for verification"); setPayFor(null); setRef(""); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const approve = async (o) => {
    setBusy(true);
    try { await api.post(`/mkt/orders/${o.id}/approve-delivery`); toast.success("Order completed — thank you!"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const sendReview = async () => {
    setBusy(true);
    try { await api.post(`/mkt/orders/${reviewFor.id}/review`, { rating, text: rtext }); toast.success("Review published"); setReviewFor(null); setRtext(""); setRating(5); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const fetchDownloads = async (o) => {
    try { const { data } = await api.get(`/mkt/orders/${o.id}`); setDownloads((d) => ({ ...d, [o.id]: data.downloads || [] })); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const KIND_URL = { service: "services", product: "products", course: "courses" };

  return (
    <div className="space-y-2" data-testid="my-orders-page">
      <PageHeader title="My Marketplace Orders" subtitle="Track purchases, payments and deliveries" testid="my-orders-title" />
      {!orders ? <LoadingRows rows={4} /> : orders.length === 0 ? (
        <EmptyState title="No orders yet" body="Browse the marketplace and hire experts for your business.">
          <Button data-testid="my-orders-browse" onClick={() => navigate("/marketplace")} className="rounded-full font-bold gradient-bg text-white border-0">Browse Marketplace</Button>
        </EmptyState>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} data-testid={`mkt-order-${o.id}`} className="bg-card border border-border rounded-xl p-5 flex flex-wrap items-center gap-4 hover:shadow-lg transition-shadow">
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={o.kind !== "custom" && o.item_id ? `/marketplace/${KIND_URL[o.kind]}/${o.item_id}` : "#"} className="font-display font-bold text-sm hover:text-primary transition-colors">{o.item_title}</Link>
                  <StatusBadge status={o.status} />
                  <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">{o.kind}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Seller: <b>{o.freelancer_name}</b> · {fmtDateTime(o.created_at)}{o.package_desc ? ` · ${o.package_desc}` : ""}</p>
                {o.delivery_note && <p className="text-xs mt-2 px-3 py-2 rounded-lg bg-muted text-muted-foreground"><b>Delivery note:</b> {o.delivery_note}</p>}
                {downloads[o.id]?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {downloads[o.id].map((d, i) => (
                      <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`} target="_blank" rel="noreferrer" data-testid={`mkt-download-${o.id}-${i}`}
                        className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                        <Download className="h-3 w-3" />{d.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-right">
                <p className="font-display font-extrabold text-lg">{fmtINR(o.amount)}</p>
                <p className="text-[10px] text-muted-foreground">#{o.id.slice(0, 8)}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {o.status === "pending_payment" && !o.payment_reference && (
                  <Button size="sm" data-testid={`mkt-order-pay-${o.id}`} onClick={() => setPayFor(o)} className="rounded-full font-bold gradient-bg text-white border-0">Submit Payment</Button>)}
                {o.status === "pending_payment" && o.payment_reference && <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">Verifying payment…</span>}
                {(o.status === "delivered" || o.status === "active") && (
                  <Button size="sm" variant="outline" data-testid={`mkt-order-approve-${o.id}`} onClick={() => approve(o)} disabled={busy} className="rounded-full font-bold">
                    <Check className="h-3.5 w-3.5 mr-1" />Approve Delivery</Button>)}
                {o.status === "completed" && !o.reviewed && (
                  <Button size="sm" variant="outline" data-testid={`mkt-order-review-${o.id}`} onClick={() => setReviewFor(o)} className="rounded-full font-bold">
                    <Star className="h-3.5 w-3.5 mr-1" />Review</Button>)}
                {o.status === "completed" && (
                  <Button size="sm" variant="ghost" data-testid={`mkt-order-contact-${o.id}`} onClick={() => navigate(`/mkt/messages/${o.freelancer_id}`)} className="rounded-full font-bold">Message Seller</Button>)}
                {o.kind === "product" && ["active", "delivered", "completed"].includes(o.status) && !downloads[o.id] && (
                  <Button size="sm" variant="ghost" data-testid={`mkt-order-downloads-${o.id}`} onClick={() => fetchDownloads(o)} className="rounded-full font-bold">
                    <Download className="h-3.5 w-3.5 mr-1" />Downloads</Button>)}
                {o.kind === "course" && ["active", "delivered", "completed"].includes(o.status) && (
                  <Button size="sm" variant="ghost" data-testid={`mkt-order-course-${o.id}`} onClick={() => navigate("/mkt/courses")} className="rounded-full font-bold">
                    Watch Course <ExternalLink className="h-3 w-3 ml-1" /></Button>)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SUBMIT PAYMENT DIALOG */}
      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent data-testid="mkt-pay-dialog">
          <DialogHeader><DialogTitle className="font-display">Submit Payment Reference</DialogTitle></DialogHeader>
          {payFor && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Pay <b>{fmtINR(payFor.amount)}</b> for <b>{payFor.item_title}</b> via the payment link shown at checkout, then paste your transaction reference below.</p>
              <div className="space-y-1.5">
                <Label>Payment reference / UPI transaction ID</Label>
                <Input data-testid="mkt-pay-ref-input" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. 412345678901" />
              </div>
              <Button data-testid="mkt-pay-submit-btn" onClick={submitPayment} disabled={busy} className="w-full rounded-full font-bold gradient-bg text-white border-0">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit for Verification"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* REVIEW DIALOG */}
      <Dialog open={!!reviewFor} onOpenChange={(o) => !o && setReviewFor(null)}>
        <DialogContent data-testid="mkt-review-dialog">
          <DialogHeader><DialogTitle className="font-display">Rate your experience</DialogTitle></DialogHeader>
          {reviewFor && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground"><b>{reviewFor.item_title}</b> by {reviewFor.freelancer_name}</p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button key={i} data-testid={`mkt-review-star-${i}`} onClick={() => setRating(i)}>
                    <Star className={`h-8 w-8 transition-colors ${i <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"}`} />
                  </button>
                ))}
              </div>
              <Textarea data-testid="mkt-review-text" value={rtext} onChange={(e) => setRtext(e.target.value)} rows={3} placeholder="How was the delivery, communication, quality?" />
              <Button data-testid="mkt-review-submit" onClick={sendReview} disabled={busy} className="w-full rounded-full font-bold gradient-bg text-white border-0">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Publish ${rating}-Star Review`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
